import { Box, Typography, TextField, InputAdornment, FormControl, InputLabel, Select, Stack, useTheme } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import { tokens } from "../theme";

const HeaderWithControls = ({
  title,
  subtitle,
  searchQuery,
  setSearchQuery,
  searchPlaceholder = "Search...",
  sortBy,
  setSortBy,
  sortOptions,
  customControls,
  icon,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { md: 'center' },
        gap: 2,
        mb: 2.5,
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        {icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.25,
              backgroundColor: "var(--fx-subtle)",
              border: "1px solid var(--fx-border)",
              color: "var(--fx-text-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              "& svg": { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        )}
        <Box>
          <Typography
            sx={{
              color: "var(--fx-text-primary)",
              fontWeight: 700,
              fontSize: "1.25rem",
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
              mb: 0.25,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: "var(--fx-text-secondary)", fontSize: "0.8125rem" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Controls — only reserve the right-hand area when controls exist */}
      {(setSearchQuery || setSortBy || customControls) && (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 480 }, justifyContent: 'flex-end', alignItems: 'center' }}
      >
        {setSearchQuery && (
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.grey[500], fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 2,
              minWidth: { sm: 240 },
              '& .MuiOutlinedInput-root': {
                height: 40,
                borderRadius: 2,
                bgcolor: 'var(--fx-paper)',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '& fieldset': { borderColor: 'var(--fx-border)' },
                '&:hover fieldset': { borderColor: 'var(--fx-border-strong)' },
                '&.Mui-focused fieldset': { borderColor: '#0087B4', borderWidth: '1px' },
                '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(0,135,180,0.12)' },
              },
              '& .MuiOutlinedInput-input': { fontSize: '0.875rem' },
            }}
          />
        )}

        {setSortBy && (
          <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value)}
              startAdornment={<SortIcon sx={{ color: colors.grey[500], mr: 0.75, ml: 0.25, fontSize: 16 }} />}
              sx={{
                height: 40,
                borderRadius: 2,
                bgcolor: 'var(--fx-paper)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--fx-text-primary)',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--fx-border)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--fx-border-strong)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0087B4', borderWidth: '1px' },
              }}
            >
              {sortOptions}
            </Select>
          </FormControl>
        )}

        {customControls}
      </Stack>
      )}
    </Box>
  );
};

export default HeaderWithControls;
